import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import { PlantDto, PlantEditDto } from 'src/store/plants/plant';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum PlantTypes {
  FetchAll = 'plants/FetchAll',
  Add = 'plants/Add',
  Update = 'plants/Update',
  Delete = 'plants/Delete',
  Failure = 'plants/Failure'
}

export type PlantAction =
  | { type: PlantTypes.FetchAll; plants: PlantDto[] }
  | { type: PlantTypes.Add; plant: PlantDto }
  | { type: PlantTypes.Update; plant: PlantDto }
  | { type: PlantTypes.Delete; plant: PlantDto }
  | { type: PlantTypes.Failure; errors: any };

export const actionCreators = {
  fetchPlants: (plants: PlantDto[]): PlantAction => ({
    type: PlantTypes.FetchAll,
    plants
  }),
  addPlant: (plant: PlantDto): PlantAction => ({
    type: PlantTypes.Add,
    plant
  }),
  updatePlant: (plant: PlantDto): PlantAction => ({
    type: PlantTypes.Update,
    plant
  }),
  deletePlant: (plant: PlantDto): PlantAction => ({
    type: PlantTypes.Delete,
    plant
  }),
  plantRequestFailure: (errors: any): PlantAction => ({
    type: PlantTypes.Failure,
    errors
  })
};

export const fetchPlants = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(PlantTypes.FetchAll));
    const { ok, data, error } = await getRequest<PlantDto[]>('/plants');
    dispatch(requestEnd(PlantTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchPlants(data))
      : dispatch(actionCreators.plantRequestFailure(error));
  };
};

export const createPlant = (plant: PlantEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>('/plants', plant);

    if (ok) {
      dispatch(alertSuccess(i18n.t('plant.created')));
      return dispatch(actionCreators.addPlant({ ...plant, id: data }));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.plantRequestFailure(error));
  };
};

export const editPlant = (id: number, plant: PlantEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/plants/${id}`, plant);

    if (ok) {
      dispatch(alertSuccess(i18n.t('plant.updated')));
      return dispatch(actionCreators.addPlant(plant as PlantDto));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.plantRequestFailure(error));
  };
};

export const deletePlant = (id: number, plant: PlantDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/plants/${id}`, plant);

    if (ok) {
      dispatch(alertSuccess(i18n.t('plant.deleted')));
      return dispatch(actionCreators.deletePlant(plant));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.plantRequestFailure(error));
  };
};
